import { useMemo, useState } from 'react';

/**
 * ねじり探索器 (007 位数 pq の記事の対話図)。
 * スライダーで素数 p と位数 q を選ぶと、正 p 角形上の位数 q のねじり
 * (倍率 a、a^q ≡ 1 で位数が丁度 q) の軌道を色分け表示し、関連情報を示す。
 */

/** p スライダーの選択肢 (素数) */
const PRIMES = [3, 5, 7, 11, 13, 17, 19, 23, 29, 31, 37];

/** 軌道の色 (アクセント #d16587 を先頭に、判別しやすい落ち着いた色相) */
const ORBIT_COLORS = [
  '#d16587',
  '#4f7ab3',
  '#6f9a49',
  '#b3873c',
  '#8a63b0',
  '#3d9e93',
  '#c06a45',
  '#7d7d7d',
  '#a94f6e',
  '#5a8ac9',
  '#93a83d',
  '#b06fa3',
];

/** a の Z/pZ に於ける位数 */
function ordMod(a: number, p: number): number {
  let x = a % p;
  let k = 1;
  while (x !== 1) {
    x = (x * a) % p;
    k += 1;
  }
  return k;
}

function divisors(n: number): number[] {
  const out: number[] = [];
  for (let d = 1; d <= n; d++) {
    if (n % d === 0) {
      out.push(d);
    }
  }
  return out;
}

/** a 倍写像の 0 以外の軌道 (小さい代表元から順に) */
function orbitsOf(a: number, p: number): number[][] {
  const seen = new Array<boolean>(p).fill(false);
  seen[0] = true;
  const orbits: number[][] = [];
  for (let x = 1; x < p; x++) {
    if (seen[x]) {
      continue;
    }
    const orbit: number[] = [];
    let y = x;
    while (!seen[y]) {
      seen[y] = true;
      orbit.push(y);
      y = (y * a) % p;
    }
    orbits.push(orbit);
  }
  return orbits;
}

const SIZE = 320;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = 118;
const R_LABEL = 143;
const R_ARROW = 103;

function vertexPos(k: number, p: number, r: number): { x: number; y: number } {
  const theta = (Math.PI / 180) * (90 - (360 / p) * k);
  return { x: CX + r * Math.cos(theta), y: CY - r * Math.sin(theta) };
}

export function TwistExplorer() {
  const [pIndex, setPIndex] = useState(2); // p = 7
  const p = PRIMES[pIndex];
  // 位数のスライダーは p-1 の約数 (>1) のみを亘る (それ以外の位数の
  // ねじりは存在しないため)
  const qDivs = useMemo(() => divisors(p - 1).filter((d) => d > 1), [p]);
  const [qIndex, setQIndex] = useState(1);
  const q = qDivs[Math.min(qIndex, qDivs.length - 1)];
  const [aChoice, setAChoice] = useState(0);

  const divs = useMemo(() => divisors(p - 1), [p]);
  const multipliers = useMemo(() => {
    const out: number[] = [];
    for (let a = 2; a < p; a++) {
      if (ordMod(a, p) === q) {
        out.push(a);
      }
    }
    return out;
  }, [p, q]);
  const a = multipliers.length > 0 ? multipliers[Math.min(aChoice, multipliers.length - 1)] : null;
  const orbits = useMemo(() => (a === null ? [] : orbitsOf(a, p)), [a, p]);

  const vertices = [];
  for (let k = 0; k < p; k++) {
    const pos = vertexPos(k, p, R);
    const lab = vertexPos(k, p, R_LABEL);
    vertices.push(
      <g key={k}>
        <circle cx={pos.x} cy={pos.y} r={p > 19 ? 2.5 : 3.5} fill="currentColor" />
        {p <= 31 && (
          <text
            x={lab.x}
            y={lab.y}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={p > 19 ? 11 : 13}
          >
            {k}
          </text>
        )}
      </g>,
    );
  }

  const arrows = orbits.map((orbit, oi) => {
    const color = ORBIT_COLORS[oi % ORBIT_COLORS.length];
    const segs = orbit.map((x, i) => {
      const y = orbit[(i + 1) % orbit.length];
      const from = vertexPos(x, p, R_ARROW);
      const to = vertexPos(y, p, R_ARROW);
      // 矢先が重ならないよう終点を少し手前に
      const dx = to.x - from.x;
      const dy = to.y - from.y;
      const len = Math.hypot(dx, dy) || 1;
      const tx = to.x - (dx / len) * 7;
      const ty = to.y - (dy / len) * 7;
      return (
        <line
          key={`${x}-${y}`}
          x1={from.x}
          y1={from.y}
          x2={tx}
          y2={ty}
          stroke={color}
          strokeWidth={1.4}
          markerEnd={`url(#twist-arrow-${oi % ORBIT_COLORS.length})`}
        />
      );
    });
    return <g key={oi}>{segs}</g>;
  });

  return (
    <div className="twist-explorer">
      <div className="twist-explorer-controls">
        <label>
          正<i>p</i>角形: <b className="twist-explorer-val">p = {p}</b>
          <input
            type="range"
            min={0}
            max={PRIMES.length - 1}
            value={pIndex}
            onChange={(e) => {
              setPIndex(Number(e.target.value));
              setAChoice(0);
            }}
          />
        </label>
        <label>
          ねじりの位数: <b className="twist-explorer-val">q = {q}</b>
          <input
            type="range"
            min={0}
            max={qDivs.length - 1}
            value={Math.min(qIndex, qDivs.length - 1)}
            onChange={(e) => {
              setQIndex(Number(e.target.value));
              setAChoice(0);
            }}
          />
        </label>
        <label className="twist-explorer-multrow">
          倍率:
          <span className="twist-explorer-mults">
            {multipliers.map((m, i) => (
              <button
                key={m}
                type="button"
                className={a === m ? 'active' : ''}
                onClick={() => setAChoice(i)}
              >
                {m}
              </button>
            ))}
          </span>
        </label>
      </div>
      <div className="twist-explorer-main">
        <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width={SIZE} height={SIZE} role="img">
          <defs>
            {ORBIT_COLORS.map((c, i) => (
              <marker
                key={i}
                id={`twist-arrow-${i}`}
                viewBox="0 0 8 8"
                refX="7"
                refY="4"
                markerWidth="7"
                markerHeight="7"
                orient="auto"
              >
                <path d="M0,0.8 L7.4,4 L0,7.2 Z" fill={c} />
              </marker>
            ))}
          </defs>
          <circle cx={CX} cy={CY} r={R} fill="none" stroke="#ccc" />
          {arrows}
          {vertices}
        </svg>
        <div className="twist-explorer-info">
          <p>
            <b>
              p = {p}, p − 1 = {p - 1}
            </b>
          </p>
          <p>p − 1 の約数: {divs.join(', ')}</p>
          {a !== null && (
            <>
              <p>
                倍率の一覧: {multipliers.join(', ')}(表示中: <b>{a}-ねじり</b>)
              </p>
              <p>
                軌道: 大きさ {q} が {(p - 1) / q} 個(0 は固定点)
              </p>
              <ul>
                {orbits.map((orbit, oi) => (
                  <li key={oi} style={{ color: ORBIT_COLORS[oi % ORBIT_COLORS.length] }}>
                    {'{'}
                    {orbit.join(', ')}
                    {'}'}
                  </li>
                ))}
              </ul>
            </>
          )}
          <p className="twist-explorer-note">
            ※ 位数のスライダーは p − 1 の約数のみを亘ります(それ以外の位数のねじりは 存在しません)。
          </p>
        </div>
      </div>
    </div>
  );
}
